<template>
  <div class="video-group-container">
    <div class="header-container mb-4">
      <div class="control-row">
        <div class="left-controls">
          <a-select
            id="validatetb-select-task-input"
            ref="validate_task_select"
            show-search
            :value="selectedTask"
            :options="options"
            placeholder="Select task"
            :filter-option="filterOption"
            style="width: 400px"
            @change="handleTaskSelect"
          />
        </div>

        <a-button type="primary" @click="showModal">
          <template #icon>
            <PlusOutlined />
          </template>
          Add Group
        </a-button>
      </div>
    </div>

    <!-- VideoPlayerModal -->
    <VideoPlayerModal
      v-model:visible="videoModalVisible"
      v-model:videos="groupVideos"
    />

    <ModelVersionValidation
      v-model:visible="showValidationModal"
      :task-id="taskId"
      :group-id="groupId"
    />

    <UploadVideoV2
      :task-id="selectedTask"
      :task-name="taskName"
      :organization="organization"
      :show-upload-modal="showUploadModal"
      :show-model-version="false"
      @set-show-upload-modal="closeUploadModal"
      @changeTask="onChangeTask"
      @handleUploadedFiles="handleUploadedFiles"
    />

    <!-- Video Group Modal -->
    <a-modal
      v-model:visible="modalVisible"
      centered
      :closable="true"
      :title="isEditing ? 'Edit Video Group' : 'Create Video Group'"
      :mask-closable="false"
      :confirm-loading="isSubmitting"
      destroy-on-close
      @cancel="handleCancel"
    >
      <a-form
        ref="formRef"
        :model="form"
        :rules="rules"
        layout="vertical"
        :validate-trigger="['blur', 'change']"
      >
        <a-form-item label="Group Name" name="name">
          <a-input
            v-model:value="form.name"
            :disabled="isEditing"
            placeholder="Enter group name"
            :max-length="50"
            allow-clear
          />
        </a-form-item>

        <a-form-item label="Task" name="task">
          <a-select
            v-model:value="form.task"
            :disabled="true"
            show-search
            placeholder="Select Task"
            :options="options"
            :filter-option="
              (input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
            "
          />
        </a-form-item>

        <a-form-item label="Description" name="description">
          <a-textarea
            v-model:value="form.description"
            :rows="3"
            placeholder="Enter detailed description"
            :max-length="500"
            show-count
            allow-clear
          />
        </a-form-item>
      </a-form>

      <template #footer>
        <a-space>
          <a-button @click="handleCancel"> Cancel </a-button>
          <a-button
            type="primary"
            :loading="isSubmitting"
            @click="handleSubmit"
          >
            {{ isEditing ? 'Update' : 'Create' }}
          </a-button>
        </a-space>
      </template>
    </a-modal>

    <!-- Groups Table -->
    <a-table
      :data-source="groups"
      :columns="columns"
      :scroll="{
        y: '55vh',
        x: true,
      }"
      :loading="isLoading"
      :pagination="{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} items`,
      }"
      row-key="id"
      class="groups-table"
    >
      <template #title>
        <a-typography-title :level="5" class="table-title">
          Validation Groups: &nbsp;
          <a-tag color="blue">
            {{ groups.length }}
          </a-tag>
        </a-typography-title>
      </template>

      <template #bodyCell="{ record, column }">
        <template v-if="column.dataIndex === 'name'">
          <a-typography-text strong>
            {{ record.name }}
          </a-typography-text>
        </template>

        <template v-else-if="column.dataIndex === 'description'">
          <a-typography-paragraph
            :ellipsis="{ rows: 2, expandable: true, symbol: 'more' }"
            class="mb-0"
          >
            {{ record.description }}
          </a-typography-paragraph>
        </template>

        <template v-else-if="column.dataIndex === 'task'">
          {{ record.task }}
        </template>

        <template v-else-if="column.dataIndex === 'actions'">
          <div class="actions-container">
            <a-tooltip title="View Videos">
              <a-button @click="goToVideos(record)">
                <template #icon>
                  <VideoCameraOutlined />
                </template>
                Show Videos
              </a-button>
            </a-tooltip>

            <a-tooltip title="Start Validation">
              <a-button type="primary" @click="startValidation(record)">
                <template #icon>
                  <PlayCircleOutlined />
                </template>
                Start Validation
              </a-button>
            </a-tooltip>

            <a-tooltip v-if="false" title="Add Videos to Group">
              <a-button @click="setShowUploadModal(true, record)">
                <template #icon>
                  <VideoCameraOutlined />
                </template>
                Add Videos
              </a-button>
            </a-tooltip>

            <a-tooltip title="Previous Runs">
              <a-button @click="togglePreviousRunsModal(record)">
                <template #icon>
                  <HistoryOutlined />
                </template>
                Previous Runs
              </a-button>
            </a-tooltip>

            <a-dropdown :trigger="['click']">
              <a-button>
                <template #icon>
                  <MoreOutlined />
                </template>
              </a-button>
              <template #overlay>
                <a-menu>
                  <a-menu-item @click="editGroup(record)">
                    <EditOutlined /> Edit
                  </a-menu-item>
                  <a-menu-item danger @click="confirmDelete(record)">
                    <DeleteOutlined /> Delete
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </div>
        </template>
      </template>

      <template #emptyText>
        <a-empty description="No validation groups found">
          <a-button type="primary" @click="showModal"> Create Group </a-button>
        </a-empty>
      </template>
    </a-table>

    <!-- Confirmation Modal -->
    <a-modal
      v-model:visible="deleteModalVisible"
      title="Confirm Deletion"
      :closable="true"
      @cancel="deleteModalVisible = false"
    >
      <p>
        Are you sure you want to delete the group "{{ selectedGroup?.name }}"?
      </p>
      <p class="text-danger">This action cannot be undone.</p>

      <template #footer>
        <a-button @click="deleteModalVisible = false"> Cancel </a-button>
        <a-button
          type="primary"
          danger
          :loading="isDeleting"
          @click="handleDelete"
        >
          Delete
        </a-button>
      </template>
    </a-modal>

    <!-- Previous Runs Modal -->
    <a-modal
      v-model:visible="previousRunsModalVisible"
      title="Previous Runs"
      :closable="true"
      width="67vw"
      class="previous-runs-modal"
      :mask-closable="true"
      :destroy-on-close="true"
      :footer="null"
      @cancel="handleCancel"
    >
      <div v-if="currentRunId === null" class="modal-content">
        <div class="list-header">
          <div class="run-details">Run Details</div>
          <div class="actions-header">Actions</div>
        </div>

        <a-list
          :data-source="previousRuns"
          :bordered="false"
          item-layout="horizontal"
          class="runs-list"
        >
          <template #renderItem="{ item }">
            <a-list-item class="run-list-item">
              <div class="run-card">
                <div class="run-info">
                  <div class="run-id-container">
                    <a-typography-text class="label">
                      Run ID:
                    </a-typography-text>
                    <a-tooltip :title="item.runId">
                      <a-typography-text code class="run-id">
                        {{ item.runId }}
                      </a-typography-text>
                    </a-tooltip>
                  </div>

                  <div class="run-time-container">
                    <a-typography-text class="label">
                      Start Time:
                    </a-typography-text>
                    <a-typography-text class="time-value">
                      {{ formatDate(item.startTime) }}
                    </a-typography-text>
                  </div>
                  <div v-if="item?.modelType" class="run-time-container">
                    <a-typography-text class="label">
                      Model Type:
                    </a-typography-text>
                    <a-typography-text class="time-value">
                      {{ item.modelType }}
                    </a-typography-text>
                  </div>
                  <div v-if="item?.modelVersion" class="run-time-container">
                    <a-typography-text class="label">
                      Model Version:
                    </a-typography-text>
                    <a-typography-text class="time-value">
                      {{ item.modelVersion }}
                    </a-typography-text>
                  </div>
                </div>

                <div class="run-actions">
                  <a-button
                    type="primary"
                    ghost
                    size="middle"
                    class="trace-button"
                    @click="
                      goToTraceAnalytics({ runId: item.runId, page: 'Trace' })
                    "
                  >
                    <template #icon>
                      <search-outlined />
                    </template>
                    Trace
                  </a-button>
                  <a-button
                    type="primary"
                    size="middle"
                    ghost
                    class="trace-buttons"
                    @click="
                      goToTraceAnalytics({
                        runId: item.runId,
                        page: 'Analytics',
                      })
                    "
                  >
                    <template #icon>
                      <bar-chart-outlined />
                    </template>
                    Analytics
                  </a-button>
                  <a-button
                    class="trace-buttons"
                    type="primary"
                    size="middle"
                    ghost
                    @click="showValidationRunModal(item.id)"
                  >
                    <template #icon>
                      <bar-chart-outlined />
                    </template>
                    Result
                  </a-button>
                </div>
              </div>
            </a-list-item>
          </template>

          <template #empty>
            <div class="empty-state">
              <a-empty description="No previous runs found">
                <template #description>
                  <div class="empty-description">
                    <p>No previous runs found</p>
                    <p class="empty-subtitle">
                      When you execute runs, they will appear here for easy
                      access.
                    </p>
                  </div>
                </template>
              </a-empty>
            </div>
          </template>
        </a-list>
      </div>
      <div v-else class="modal-content">
        <ValidationGroupResult
          :run-id="currentRunId"
          :task-name="taskName"
          :organization="organization"
          @handle-back="setCurrentRunId"
        />
      </div>
    </a-modal>
  </div>
  <OperationProgress
    v-if="validationProgress.length"
    :process-list="validationProgress"
    :process-type="'Group Validation Records'"
  />
</template>

<script>
import { useHandleSpace } from '@/composables/useHandleSpace';
import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import OperationProgress from 'src/components/shared/Components/OperationProgress.vue';
import { longRunningTaskStatus } from 'src/components/user-panel/pages/process/ObjectDetection/config.js';
import trainingStatuses from 'src/config/training-status-config.js';
import router from 'src/router';
import httpClient from 'src/service/httpClient';
import LRTService from 'src/services/longRunningTask';
import S3Service from 'src/services/s3';
import TelemetryService from 'src/services/telemetry';
import types from 'src/store/mutation-types';
import { getSortedTask } from 'src/utils/task';
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from 'vue';
import { useStore } from 'vuex';
import getFileNameFromUrl from '../../../shared/Helpers/getFileNameFromUrl';
import ModelVersionValidation from '../process/ModelVersions/ModelVersionValidation.vue';
import UploadVideoV2 from './UploadVideoV2.vue';
import ValidationGroupResult from './ValidationGroupResult.vue';
import VideoPlayerModal from './VideoPlayerModal.vue';
const { filterOption } = useHandleSpace();

const toast = inject('toast');

export default defineComponent({
  name: 'VideoGroupManagement',

  components: {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    VideoCameraOutlined,
    PlayCircleOutlined,
    MoreOutlined,
    HistoryOutlined,
    VideoPlayerModal,
    UploadVideoV2,
    ModelVersionValidation,
    OperationProgress,
    ValidationGroupResult,
  },

  emits: ['navigateToVideos'],

  setup(props, { emit }) {
    // State
    const store = useStore();
    const formRef = ref(null);
    const modalVisible = ref(false);
    const previousRunsModalVisible = ref(false);
    const deleteModalVisible = ref(false);
    const isSubmitting = ref(false);
    const isLoading = ref(false);
    const isDeleting = ref(false);
    const isEditing = ref(false);
    const selectedGroup = ref(null);
    const loadingStates = ref({});
    const showUploadModal = ref(false);
    const taskId = ref(null);
    const taskName = ref(null);
    const options = ref(null);
    const taskList = ref(null);
    const validateTaskSelect = ref(null);
    const showValidationModal = ref(false);

    const allTasks = ref(store.getters['allTasks']);
    const groupId = ref(null);
    const loading = ref(false);
    const interval = ref(null);
    const validationProgress = ref([]);

    const hasValidationProgress = computed(() => {
      return validationProgress.value && validationProgress.value.length > 0;
    });

    const previousRuns = ref([
      {
        runId: 'run_001',
        startTime: '2025-04-21T10:30:00Z',
      },
      {
        runId: 'run_002',
        startTime: '2025-04-20T15:45:00Z',
      },
      // More runs...
    ]);

    // Form state
    const form = reactive({
      id: null,
      name: '',
      task: { label: '', value: null },
      description: '',
    });

    // Form validation rules
    const rules = {
      name: [
        {
          required: true,
          message: 'Please enter a group name',
          trigger: 'blur',
        },
        {
          min: 2,
          max: 50,
          message: 'Group name must be between 2 and 50 characters',
          trigger: 'blur',
        },
      ],
      task: [
        { required: true, message: 'Please select a task', trigger: 'change' },
      ],
      description: [
        {
          max: 500,
          message: 'Description cannot exceed 500 characters',
          trigger: 'blur',
        },
      ],
    };

    // Table columns
    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        ellipsis: true,
      },
      {
        title: 'Task',
        dataIndex: 'task_name',
        key: 'task',
        ellipsis: true,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        width: '400px',
        fixed: 'right',
      },
    ];

    const videoModalVisible = ref(false);
    const groupVideos = ref([]);
    const groups = ref([]);
    const currentRunId = ref(null);

    // computed
    const selectedTask = computed(() => store.getters.selectedTask);
    const organization = computed(() => store.getters.organization);
    const modelVersionsList = computed(() => store.getters.modelVersionsList);
    const taskOptions = computed(() => {
      return getSortedTask(allTasks.value);
    });

    const showModelVersionsModal = computed(
      () => store.getters['showModelVersionsModal']
    );

    // Action
    const showModelVersions = (payload) => {
      store.dispatch('showModal', payload);
    };

    // Watch
    // watch(store.getters.allTasks.length, (value) => {});

    // Methods

    const showValidationRunModal = (runId) => {
      setCurrentRunId(runId);
    };

    const setCurrentRunId = (runId = null) => {
      currentRunId.value = runId;
    };

    const getTrainedTasksList = async () => {
      const res = await httpClient.get(
        `organization/task/get_all_trained_tasks_of_organization?train_status=${trainingStatuses.trained}`
      );
      if (res === 'error') {
        toast.error('Error occured while fetching tasks!');
        return;
      }
      taskList.value = res.map((el) => ({
        id: el.task.id,
        taskName: el.task.taskName,
      }));
      options.value = getSortedTask(res.map((el) => el.task));
    };

    const onChangeTask = async (taskId) => {
      getValidationRecord(taskId);
    };

    const handleTaskSelect = async (val) => {
      store.dispatch('setSelectedTask', val);
      validateTaskSelect.value?.blur();
      await store.dispatch('fetchModelVersionsList', val);
      let res = await store.dispatch('groupValidateVideos', {
        task: selectedTask.value,
      });
      groups.value = res;
      store.dispatch('fetchTaskDetails', val);
    };

    const getPopupContainer = (trigger) => {
      return trigger.parentNode;
    };

    const setShowUploadModal = (value, record) => {
      let task = options.value.find((opt) => opt.id === record.task);
      taskId.value = record.task;
      taskName.value = task.taskName;
      showUploadModal.value = value;
      groupId.value = record.id;
    };

    const startValidation = (record_id) => {
      taskId.value = record_id.task;
      groupId.value = record_id.id;
      showValidationModal.value = true;
    };

    const closeUploadModal = (value) => {
      showUploadModal.value = value;
      groupId.value = null;
    };

    const getPayload = () => {
      return {
        Task: selectedTask.value,
        organization: organization.value,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        group: groupId.value,
      };
    };

    const handleUploadedFiles = async (fileUrlList, modelVersion) => {
      showUploadModal.value = false;
      loading.value = true;
      const payload = getPayload();
      const responses = getAsyncResponses(fileUrlList, payload);
      toast.success('Videos added to the group');
    };

    const getAsyncResponses = (fileUrlList, payload) => {
      return fileUrlList.map((fileUrl) =>
        addValidationRecord({
          ...payload,
          ['video_url']: fileUrl['file_url'],
          ['thumbnail_path']: fileUrl['thumbnail_path'],
          ['file_name']: fileUrl['thumbnail_path']
            .split('Videos/')
            .pop()
            .replace('/', ''),
        })
      );
    };

    const addValidationRecord = async (data) => {
      return await httpClient.post(
        'websocket_inference/validation_record/',
        data,
        false,
        true,
        false
      );
    };

    const getValidationRecord = async (taskId, page = 1, loading = true) => {
      loading.value = loading.value && true;
      const tagNames = this.getTagString();

      if (tagNames.length === 0) {
        this.videoList = [];
        this.videosCount = 0;
        loading.value = false;
        return;
      }
      let url = this.getUrl(taskId, page, tagNames);

      const res = await httpClient.getData(url, false);
      if (res === 'error') {
        toast.error('Error occured while fetching Videos!');
        loading.value = false;
        return;
      }
      this.videoList = res.results.map((videoObj) => {
        const { video_url, ...res } = videoObj;
        delete videoObj.per_frame_prediction_file_url;
        return {
          ...res,
          fileName: getFileNameFromUrl(video_url),
          isThumbnailExist: true,
        };
      });
      this.videosCount = res.count;
      loading.value = false;
      return;
    };

    const showModal = async () => {
      resetForm();
      isEditing.value = false;
      modalVisible.value = true;
      form.task = selectedTask.value;
      await store.dispatch('getAllTasks');
    };

    const resetForm = () => {
      form.id = null;
      form.name = '';
      form.task = '';
      form.description = '';

      if (formRef.value) {
        formRef.value.resetFields();
      }
    };

    const handleCancel = () => {
      modalVisible.value = false;
      currentRunId.value = null;
      resetForm();
    };

    const handleSubmit = async () => {
      try {
        await formRef.value.validate();
        isSubmitting.value = true;

        if (isEditing.value) {
          store.dispatch('updateGroupValidateVideos', {
            id: form.id,
            payload: {
              name: form.name,
              task: selectedTask.value,
              description: form.description,
            },
          });
          const index = groups.value.findIndex((g) => g.id === form.id);
          if (index !== -1) {
            groups.value[index] = {
              ...groups.value[index],
              name: form.name,
              task: selectedTask.value,
              description: form.description,
            };
            message.success('Group updated successfully');
          }
        } else {
          await store.dispatch('createGroupValidateVideos', {
            name: form.name,
            task: selectedTask.value,
            description: form.description,
          });
          // Create new group
          console.log('form.task.label: ', form.task);
          // await fetchGroups(form.task);
          // groups.value.push(newGroup);
          message.success('Group created successfully');
        }

        await fetchGroups(selectedTask.value);

        modalVisible.value = false;
        resetForm();
      } catch (error) {
        message.error('Please check the form for errors');
        console.error('Form validation failed:', error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const editGroup = (record) => {
      form.id = record.id;
      form.name = record.name;
      form.task = record.task;
      form.description = record.description;

      isEditing.value = true;
      modalVisible.value = true;
    };

    const confirmDelete = (record) => {
      selectedGroup.value = record;
      deleteModalVisible.value = true;
    };

    const goToTraceAnalytics = ({ runId, page }) => {
      const dateFormat = 'MM-DD-YY HH:mm:ss';
      const params = {
        runId: runId,
        taskId: selectedTask.value,
      };

      if (page === 'Trace') {
        params.toast = true;
      }
      let routeData = router.resolve({
        name: page,
        query: params,
      });
      window.open(routeData.href, '_blank');
    };

    const handleDelete = async () => {
      if (!selectedGroup.value) return;

      isDeleting.value = true;

      try {
        await new Promise((resolve) => setTimeout(resolve, 800));

        await store.dispatch(
          'deleteGroupValidateVideos',
          selectedGroup.value.id
        );
        groups.value = groups.value.filter(
          (g) => g.id !== selectedGroup.value.id
        );

        message.success(
          `Group "${selectedGroup.value.name}" deleted successfully`
        );
        deleteModalVisible.value = false;
        selectedGroup.value = null;
      } catch (error) {
        message.error('Failed to delete group');
        console.error('Delete error:', error);
      } finally {
        isDeleting.value = false;
      }
    };

    const fetchPresignedUrl = (bucket_name, object_path) => {
      const payload = {
        bucket_name,
        object_path,
      };
      return new Promise(async (resolve) => {
        const res = await httpClient.post(
          'generic/generate_new_url/',
          payload,
          false,
          false,
          false
        );
        resolve(res.presigned_url || res);
      });
    };

    const parseS3Path = (s3Key) => {
      const firstSlashIndex = s3Key.indexOf('/');
      const bucketName = s3Key.slice(0, firstSlashIndex);
      let path = s3Key.slice(firstSlashIndex + 1);

      path = path.replace(/\/$/, '');

      return {
        bucketName,
        path,
      };
    };

    const thumbnailUrl = (path) => {
      return (
        'https://retrocausal-video-thumbnails.s3.amazonaws.com/' +
        path +
        '512.jpg'
      );
    };

    const goToVideos = async (record) => {
      let videos = await store.dispatch(
        'fetchValidatedVideoByGroup',
        record.id
      );
      let videosThumbnailPreSignedMapping = videos.map((data) =>
        S3Service.getPresignedPostURL({
          bucket: `${localStorage.getItem('organization')}-videos`,
          key: data.thumbnail_path,
        })
      );

      let videosThumbnailPreSigned = await Promise.all(
        videosThumbnailPreSignedMapping
      );

      let videosPreSignedMapping = videos.map((data) => {
        let { bucketName, path } = parseS3Path(data.thumbnail_path);
        return fetchPresignedUrl(bucketName, path);
      });

      let videosPreSigned = await Promise.all(videosPreSignedMapping);
      console.log('videosPreSigned: ', videosPreSigned);
      videos = videos.map((video, index) => {
        let [error, data] = videosThumbnailPreSigned[index];
        video.thumbnail = thumbnailUrl(data.fields.key.replace(' ', '%20'));
        video.url = videosPreSigned[index];
        return video;
      });

      groupVideos.value = videos;
      videoModalVisible.value = true;
      // emit('navigateToVideos', record);
    };

    async function fetchGroups(taskId) {
      let validationVideos = await store.dispatch('groupValidateVideos', {
        task: taskId,
      });
      groups.value = validationVideos;
    }

    const togglePreviousRunsModal = async (record) => {
      try {
        let [error, resp] = await TelemetryService.fetchPerviousGroupRuns(
          record.id
        );
        previousRuns.value = resp.map((data) => ({
          id: data.id,
          runId: data.run_id,
          startTime: data.date_created,
          modelVersion: data.model_version,
          modelType: data.model_type,
        }));

        previousRunsModalVisible.value = !previousRunsModalVisible.value;
        let task = options.value.find((opt) => opt.id === record.task);
        taskName.value = task.taskName;
      } catch (error) {
        console.log('error occured while fetching previous runs', error);
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';

      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    };

    const truncateRunId = (runId) => {
      return runId.length > 16 ? `${runId.substring(0, 16)}...` : runId;
    };

    const getGroupValidationProgress = async () => {
      if (!selectedTask.value) {
        return;
      }
      const taskFilter = {
        name: 'group_validation_record',
        task: selectedTask.value,
      };
      const [_, started] = await LRTService.getLongRunningTasks(false, {
        ...taskFilter,
        status: longRunningTaskStatus.STARTED,
      });
      validationProgress.value = [];

      getStartedValidationRecords(started);
    };

    const getStartedValidationRecords = (started) => {
      started.forEach((item) => {
        let name = `Group Validation`;
        if (item.event?.file_name) name += ' ' + item.event?.file_name;
        const payload = {
          id: item.id,
          name: name,
          status: longRunningTaskStatus.STARTED,
          progress: item.details.progress,
        };
        validationProgress.value.push(payload);
      });
    };

    onMounted(async () => {
      await getTrainedTasksList();
      interval.value = setInterval(getGroupValidationProgress, 15000);
    });

    onUnmounted(() => {
      clearInterval(interval.value);
    });

    return {
      // State
      tasks: allTasks,
      formRef,
      modalVisible,
      previousRunsModalVisible,
      deleteModalVisible,
      isSubmitting,
      isLoading,
      isDeleting,
      isEditing,
      selectedGroup,
      loadingStates,
      showUploadModal,
      taskId,
      taskName,
      options,
      taskList,
      loading,
      toast,
      showValidationModal,
      showModelVersionsModal,
      showModelVersions,

      // Data
      videoModalVisible,
      groupVideos,
      form,
      rules,
      columns,
      groups,
      previousRuns,
      groupId,
      organization,
      types,
      interval,
      validationProgress,
      currentRunId,

      // Methods
      fetchPresignedUrl,
      handleTaskSelect,
      getPopupContainer,
      showModal,
      handleCancel,
      handleSubmit,
      editGroup,
      confirmDelete,
      handleDelete,
      goToVideos,
      formatDate,
      togglePreviousRunsModal,
      getPayload,
      setShowUploadModal,
      closeUploadModal,
      handleUploadedFiles,
      getAsyncResponses,
      addValidationRecord,
      getValidationRecord,
      getTrainedTasksList,
      filterOption,
      onChangeTask,
      startValidation,
      goToTraceAnalytics,
      truncateRunId,
      showValidationRunModal,
      setCurrentRunId,
    };
  },
});
</script>

<style scoped>
.video-group-container {
  padding: 16px;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  margin-bottom: 0 !important;
  display: flex;
  align-items: center;
}

.groups-table {
  width: 100%;
}

.actions-container {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .actions-container {
    flex-direction: column;
    gap: 8px;
  }
}

.text-danger {
  color: #ff4d4f;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-4 {
  margin-bottom: 16px;
}

.previous-runs-modal {
  --primary-color: #1890ff;
  --border-radius: 8px;
  --shadow-color: rgba(0, 0, 0, 0.05);
  --hover-shadow: rgba(0, 0, 0, 0.1);
}

.previous-runs-modal :deep(.ant-modal-content) {
  border-radius: var(--border-radius);
  overflow: hidden;
}

.previous-runs-modal :deep(.ant-modal-header) {
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.previous-runs-modal :deep(.ant-modal-title) {
  font-size: 18px;
  font-weight: 600;
  color: #262626;
}

.previous-runs-modal :deep(.ant-modal-body) {
  padding: 0;
}

.runs-list {
  max-height: 60vh;
  overflow-y: auto;
}

.list-header {
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  background-color: #fafafa;
  font-weight: 500;
  color: #595959;
  border-bottom: 1px solid #f0f0f0;
}

.actions-header {
  width: 220px;
  text-align: center;
}

.run-list-item {
  padding: 0 !important;
  border-bottom: 1px solid #f0f0f0 !important;
}

.run-card {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  transition: background-color 0.2s ease;
}

.run-card:hover {
  background-color: #f5f5f5;
}

.run-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.run-id-container,
.run-time-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label {
  color: #8c8c8c;
  font-size: 14px;
  min-width: 80px;
}

.run-id {
  color: var(--primary-color) !important;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier,
    monospace;
  font-size: 14px;
  background-color: rgba(24, 144, 255, 0.1);
  border: 1px solid rgba(24, 144, 255, 0.2);
  border-radius: 4px;
  padding: 2px 6px;
  /* max-width: 200px; */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.time-value {
  color: #595959;
  font-size: 14px;
}

.run-actions {
  display: flex;
  gap: 12px;
}

.trace-button,
.analytics-button {
  min-width: 100px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.02);
}

.trace-button:hover,
.analytics-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px var(--shadow-color);
}

.trace-button {
  border-color: var(--primary-color);
}

.analytics-button {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.empty-state {
  padding: 40px 0;
}

.previous-runs-modal :deep(.ant-modal-content) {
  border-radius: 8px;
  overflow: hidden;
}

.previous-runs-modal :deep(.ant-modal-header) {
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.previous-runs-modal :deep(.ant-modal-body) {
  padding: 0;
}

.modal-content {
  max-height: 60vh;
  overflow-y: auto;
}

.list-header {
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  font-weight: 500;
  color: #5c5c5c;
}

.run-details {
  flex: 1;
}

.runs-list :deep(.ant-list-item) {
  padding: 0;
}

.run-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 24px;
  transition: background-color 0.2s;
}

.run-card:hover {
  background-color: #fafafa;
}

.run-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.run-id-container,
.run-time-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label {
  color: #5c5c5c;
  font-weight: 500;
}

.run-id {
  /* max-width: 200px; */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.run-actions {
  display: flex;
  gap: 8px;
}

.trace-button,
.analytics-button {
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-state {
  padding: 40px 24px;
  text-align: center;
}

.empty-description {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.empty-subtitle {
  color: #8c8c8c;
  font-size: 12px;
}

/* Responsive styles */
@media (max-width: 576px) {
  .run-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .run-actions {
    width: 100%;
    justify-content: space-between;
  }

  .trace-button,
  .analytics-button {
    flex: 1;
  }

  .run-id {
    max-width: 150px;
  }
}
.header-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.control-row {
  display: flex;
  justify-content: space-between; /* ⬅️ pushes children to far ends */
  align-items: center;
  width: 100%;
}
</style>
